import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, decimal, columnStats, heatStyle, buildRanks, chainColor,
	RankBadge, Delta, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Метрики, по которым строится рейтинг филиалов
const RANK_METRICS = [
	{ id: 'revenue', goodHigh: true },
	{ id: 'avg_cheque', goodHigh: true },
	{ id: 'gross_profit', goodHigh: true },
	{ id: 'revenue_delta', goodHigh: true },
]

const CHANNEL_COLORS = { hall: '#5b73e8', takeaway: '#f59f00', delivery: '#12b886' }

function Sales() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [rankBy, setRankBy] = useState('revenue')
	const [chartMetric, setChartMetric] = useState('revenue')
	const [chartMode, setChartMode] = useState('branches')

	const filters = useChainFilters({ order_type_ids: [], source_ids: [] }, searchWithFilter)
	const options = useAnalyticsOptions(['sources'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/sales', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const { sorted, sort, toggle } = useSort(branches, 'revenue')

	const rankMetric = RANK_METRICS.find(item => item.id === rankBy)
	const { ranks, count } = useMemo(() => buildRanks(branches, rankBy, rankMetric.goodHigh), [branches, rankBy, rankMetric])

	// Шкалы заливки по колонкам: лучший зелёный, худший красный
	const stats = useMemo(() => ({
		avg_cheque: columnStats(branches, 'avg_cheque'),
		avg_guest: columnStats(branches, 'avg_guest'),
		items_per_cheque: columnStats(branches, 'items_per_cheque'),
		gross_profit: columnStats(branches, 'gross_profit'),
		margin: columnStats(branches, 'margin'),
		food_cost: columnStats(branches, 'food_cost'),
		discount_percent: columnStats(branches, 'discount_percent'),
		returns: columnStats(branches, 'returns'),
	}), [branches])

	const maxRevenue = useMemo(() => columnStats(branches, 'revenue').max, [branches])
	const best = sorted.length > 0 ? [...branches].sort((a, b) => b.revenue - a.revenue)[0] : null
	const worst = branches.length > 1 ? [...branches].sort((a, b) => a.revenue - b.revenue)[0] : null

	const rankLabels = {
		revenue: t('revenue'),
		avg_cheque: t('avg_cheque'),
		gross_profit: t('chain_gross_profit'),
		revenue_delta: t('chain_growth'),
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const columns = [
			{ label: t('chain_rank'), type: 'int', width: 8 },
			{ label: t('chain_branch'), type: 'text', width: 30 },
			{ label: t('revenue'), type: 'money' },
			{ label: t('share'), type: 'percent', width: 9 },
			{ label: t('chain_prev_period'), type: 'money' },
			{ label: t('chain_growth'), type: 'percent', width: 9 },
			{ label: t('cheques'), type: 'int', width: 10 },
			{ label: t('avg_cheque'), type: 'money' },
			{ label: t('guests'), type: 'int', width: 10 },
			{ label: t('chain_avg_guest'), type: 'money' },
			{ label: t('chain_items_per_cheque'), type: 'number', width: 11 },
			{ label: t('cost_price'), type: 'money' },
			{ label: t('chain_gross_profit'), type: 'money' },
			{ label: t('margin'), type: 'percent', width: 9 },
			{ label: t('chain_food_cost'), type: 'percent', width: 10 },
			{ label: t('discounts'), type: 'money' },
			{ label: t('chain_returns'), type: 'money' },
			{ label: t('hall'), type: 'money' },
			{ label: t('takeaway'), type: 'money' },
			{ label: t('delivery'), type: 'money' },
			{ label: t('chain_service_delivery'), type: 'money', width: 18 },
		]
		const row = (item, rank) => [
			rank, item.pos_name, item.revenue, item.share, item.prev_revenue, item.revenue_delta,
			item.cheques, item.avg_cheque, item.guests, item.avg_guest, item.items_per_cheque,
			item.cost, item.gross_profit, item.margin, item.food_cost, item.discount, item.returns,
			item.revenue_hall, item.revenue_takeaway, item.revenue_delivery, item.service_delivery,
		]
		const byRevenue = buildRanks(branches, 'revenue', true).ranks
		const daily = data.daily || { days: [], series: [] }

		downloadExcel(`${t('chain_sales_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_branches'),
				title: t('chain_sales_title'),
				subtitle: period,
				columns,
				rows: [...branches].sort((a, b) => b.revenue - a.revenue).map(item => row(item, byRevenue[item.pos_id])),
				footer: row({ ...totals, pos_name: t('total') }, ''),
			},
			{
				name: t('chain_by_days'),
				title: `${t('chain_by_days')} — ${t('revenue')}`,
				subtitle: period,
				columns: [
					{ label: t('date'), type: 'text', width: 12 },
					...daily.series.map(series => ({ label: series.pos_name, type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: daily.days.map((day, index) => {
					const values = daily.series.map(series => series.revenue[index])
					return [day.split('-').reverse().join('.'), ...values, values.reduce((sum, value) => sum + Number(value || 0), 0)]
				}),
			},
		])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_sales_title')}
				icon="uil-chart-growth"
				filters={filters}
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} />
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('revenue')} icon="uil-money-bill" color="#12b886"
						value={money(totals.revenue)}
						sub={<><Delta value={totals.revenue_delta} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('cheques')} icon="uil-receipt" color="#5b73e8"
						value={formatMoney(totals.cheques)}
						sub={<><Delta value={totals.cheques_delta} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('avg_cheque')} icon="uil-calculator-alt" color="#f59f00"
						value={money(totals.avg_cheque)}
						sub={<><Delta value={totals.avg_cheque_delta} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_gross_profit')} icon="uil-chart-pie-alt" color="#7c4dff"
						value={money(totals.gross_profit)}
						sub={<>{t('margin')} <b>{percent(totals.margin)}</b> · {t('chain_food_cost')} <b>{percent(totals.food_cost)}</b></>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_best_branch')} icon="uil-trophy" color="#12b886"
						value={best ? best.pos_name : '—'}
						sub={best ? <>{money(best.revenue)} · {percent(best.share)}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_worst_branch')} icon="uil-arrow-down-right" color="#f06548"
						value={worst ? worst.pos_name : '—'}
						sub={worst ? <>{money(worst.revenue)} · {percent(worst.share)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_rating')}</h5>
							<span className="chain-muted">{t('chain_rank_by')}</span>
							<FilterSegment
								options={RANK_METRICS.map(item => ({ id: item.id, label: rankLabels[item.id] }))}
								value={rankBy}
								onChange={setRankBy}
							/>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
									<SortTh sortKey="revenue" sort={sort} onSort={toggle} className="text-end">{t('revenue')}</SortTh>
									<SortTh sortKey="revenue_delta" sort={sort} onSort={toggle} className="text-end" title={t('chain_vs_prev')}>{t('chain_growth')}</SortTh>
									<SortTh sortKey="cheques" sort={sort} onSort={toggle} className="text-end">{t('cheques')}</SortTh>
									<SortTh sortKey="avg_cheque" sort={sort} onSort={toggle} className="text-end">{t('avg_cheque')}</SortTh>
									<SortTh sortKey="guests" sort={sort} onSort={toggle} className="text-end">{t('guests')}</SortTh>
									<SortTh sortKey="avg_guest" sort={sort} onSort={toggle} className="text-end">{t('chain_avg_guest')}</SortTh>
									<SortTh sortKey="items_per_cheque" sort={sort} onSort={toggle} className="text-end" title={t('chain_items_per_cheque_hint')}>{t('chain_items_per_cheque')}</SortTh>
									<SortTh sortKey="gross_profit" sort={sort} onSort={toggle} className="text-end">{t('chain_gross_profit')}</SortTh>
									<SortTh sortKey="margin" sort={sort} onSort={toggle} className="text-end">{t('margin')}</SortTh>
									<SortTh sortKey="food_cost" sort={sort} onSort={toggle} className="text-end" title={t('chain_food_cost_hint')}>{t('chain_food_cost')}</SortTh>
									<SortTh sortKey="discount" sort={sort} onSort={toggle} className="text-end">{t('discounts')}</SortTh>
									<SortTh sortKey="returns" sort={sort} onSort={toggle} className="text-end">{t('chain_returns')}</SortTh>
									<th style={{ minWidth: '150px' }}>{t('chain_channels')}</th>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={15} />}
								{sorted.map(item => {
									const index = branches.indexOf(item)
									const channelTotal = item.revenue_hall + item.revenue_takeaway + item.revenue_delivery
									const channelWidth = (value) => channelTotal > 0 ? `${value / channelTotal * 100}%` : '0%'
									return (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{item.pos_name}
												</span>
											</td>
											<FillCell percent={maxRevenue > 0 ? item.revenue / maxRevenue * 100 : 0} tone="success" title={`${t('share')}: ${percent(item.share)}`}>
												<b>{money(item.revenue)}</b>
												<div className="chain-muted">{percent(item.share)}</div>
											</FillCell>
											<td className="chain-num">
												<Delta value={item.revenue_delta} />
												<div className="chain-muted">{money(item.prev_revenue)}</div>
											</td>
											<td className="chain-num">
												{formatMoney(item.cheques)}
												<div><Delta value={item.cheques_delta} showNone={false} /></div>
											</td>
											<td className="chain-num" style={heatStyle(item.avg_cheque, stats.avg_cheque)}>
												{money(item.avg_cheque)}
												<div><Delta value={item.avg_cheque_delta} showNone={false} /></div>
											</td>
											<td className="chain-num">{formatMoney(item.guests)}</td>
											<td className="chain-num" style={heatStyle(item.avg_guest, stats.avg_guest)}>{money(item.avg_guest)}</td>
											<td className="chain-num" style={heatStyle(item.items_per_cheque, stats.items_per_cheque)}>{decimal(item.items_per_cheque)}</td>
											<td className="chain-num" style={heatStyle(item.gross_profit, stats.gross_profit)}>{money(item.gross_profit)}</td>
											<td className="chain-num" style={heatStyle(item.margin, stats.margin)}>{percent(item.margin)}</td>
											<td className="chain-num" style={heatStyle(item.food_cost, stats.food_cost, false)}>{percent(item.food_cost)}</td>
											<td className="chain-num" style={heatStyle(item.discount_percent, stats.discount_percent, false)}>
												{money(item.discount)}
												<div className="chain-muted">{percent(item.discount_percent)}</div>
											</td>
											<td className="chain-num" style={heatStyle(item.returns, stats.returns, false)}>
												{money(item.returns)}
												<div className="chain-muted">{formatMoney(item.returns_count)} {t('chain_pcs')}</div>
											</td>
											<td title={`${t('hall')}: ${money(item.revenue_hall)}\n${t('takeaway')}: ${money(item.revenue_takeaway)}\n${t('delivery')}: ${money(item.revenue_delivery)}`}>
												<div className="chain-split">
													<span style={{ width: channelWidth(item.revenue_hall), background: CHANNEL_COLORS.hall }}></span>
													<span style={{ width: channelWidth(item.revenue_takeaway), background: CHANNEL_COLORS.takeaway }}></span>
													<span style={{ width: channelWidth(item.revenue_delivery), background: CHANNEL_COLORS.delivery }}></span>
												</div>
												<div className="chain-muted mt-1">
													{percent(channelTotal > 0 ? item.revenue_hall / channelTotal * 100 : 0, 0)} · {percent(channelTotal > 0 ? item.revenue_takeaway / channelTotal * 100 : 0, 0)} · {percent(channelTotal > 0 ? item.revenue_delivery / channelTotal * 100 : 0, 0)}
												</div>
											</td>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num">{money(totals.revenue)}</td>
										<td className="chain-num"><Delta value={totals.revenue_delta} /></td>
										<td className="chain-num">{formatMoney(totals.cheques)}</td>
										<td className="chain-num">{money(totals.avg_cheque)}</td>
										<td className="chain-num">{formatMoney(totals.guests)}</td>
										<td className="chain-num">{money(totals.avg_guest)}</td>
										<td className="chain-num">{decimal(totals.items_per_cheque)}</td>
										<td className="chain-num">{money(totals.gross_profit)}</td>
										<td className="chain-num">{percent(totals.margin)}</td>
										<td className="chain-num">{percent(totals.food_cost)}</td>
										<td className="chain-num">{money(totals.discount)}</td>
										<td className="chain-num">{money(totals.returns)}</td>
										<td>
											<span className="chain-muted">
												<span className="chain-dot" style={{ background: CHANNEL_COLORS.hall }}></span>{t('hall')}
												<span className="chain-dot ms-2" style={{ background: CHANNEL_COLORS.takeaway }}></span>{t('takeaway')}
												<span className="chain-dot ms-2" style={{ background: CHANNEL_COLORS.delivery }}></span>{t('delivery')}
											</span>
										</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-xl-8">
					<DailyChart data={data} metric={chartMetric} mode={chartMode} onMetric={setChartMetric} onMode={setChartMode} />
				</div>
				<div className="col-xl-4">
					<ChannelsChart branches={branches} />
				</div>
			</div>
		</>
	)
}

// Выручка / чеки по дням: каждый филиал своей линией или сеть одной
function DailyChart({ data, metric, mode, onMetric, onMode }) {
	const { t } = useTranslation()
	const daily = useMemo(() => data?.daily || { days: [], series: [] }, [data])

	const series = useMemo(() => {
		if (mode === 'total') {
			return [{
				name: t('chain_network'),
				data: daily.days.map((day, index) => daily.series.reduce((sum, item) => sum + Number(item[metric][index] || 0), 0)),
			}]
		}
		return daily.series.map(item => ({ name: item.pos_name, data: item[metric] }))
	}, [daily, metric, mode, t])

	const options = {
		chart: { type: mode === 'total' ? 'area' : 'line', toolbar: { show: false }, zoom: { enabled: false } },
		colors: mode === 'total' ? ['#5b73e8'] : daily.series.map((item, index) => chainColor(index)),
		stroke: { width: mode === 'total' ? 3 : 2, curve: 'smooth' },
		fill: mode === 'total' ? { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.02 } } : { opacity: 1 },
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: {
			categories: daily.days.map(day => day.slice(8, 10) + '.' + day.slice(5, 7)),
			tickAmount: Math.min(daily.days.length, 15),
			labels: { rotate: 0, hideOverlappingLabels: true },
		},
		yaxis: { labels: { formatter: value => formatMoney(Math.round(value)) } },
		tooltip: { shared: true, y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_by_days')}</h5>
					<div className="chain-toolbar-group">
						<FilterSegment
							options={[{ id: 'revenue', label: t('revenue') }, { id: 'cheques', label: t('cheques') }]}
							value={metric}
							onChange={onMetric}
						/>
						<FilterSegment
							options={[{ id: 'branches', label: t('chain_by_branches') }, { id: 'total', label: t('chain_network') }]}
							value={mode}
							onChange={onMode}
						/>
					</div>
				</div>
				<ReactApexChart key={`${metric}-${mode}`} type={options.chart.type} height={330} series={series} options={options} />
			</div>
		</div>
	)
}

// Выручка филиалов по каналам: зал / с собой / доставка
function ChannelsChart({ branches }) {
	const { t } = useTranslation()
	const rows = [...branches].sort((a, b) => b.revenue - a.revenue)

	const series = [
		{ name: t('hall'), data: rows.map(item => Math.round(item.revenue_hall)) },
		{ name: t('takeaway'), data: rows.map(item => Math.round(item.revenue_takeaway)) },
		{ name: t('delivery'), data: rows.map(item => Math.round(item.revenue_delivery)) },
	]
	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%' } },
		colors: [CHANNEL_COLORS.hall, CHANNEL_COLORS.takeaway, CHANNEL_COLORS.delivery],
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(item => item.pos_name), labels: { formatter: value => formatMoney(Math.round(value / 1000)) + 'k' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title">
					<h5>{t('chain_channels')}</h5>
				</div>
				<ReactApexChart type="bar" height={Math.max(330, rows.length * 38 + 80)} series={series} options={options} />
			</div>
		</div>
	)
}

export default Sales
